import React, { useState, useEffect, useContext, useCallback, useMemo, useRef } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import AuthContext from '../context/AuthContext';
import {
    Box, Container, Typography, Button, CircularProgress, Paper, Chip,
    TextField, Dialog, DialogTitle, DialogContent, DialogActions,
    IconButton, Tooltip, ToggleButton, ToggleButtonGroup,
    List, ListItem, ListItemText, Divider,
    Alert, Snackbar, FormControl, InputLabel, Select, MenuItem,
    Tab, Tabs,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RouteIcon from '@mui/icons-material/Route';
import SchoolIcon from '@mui/icons-material/School';
import HomeIcon from '@mui/icons-material/Home';
import EventIcon from '@mui/icons-material/Event';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import CloseIcon from '@mui/icons-material/Close';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import LocationOffIcon from '@mui/icons-material/LocationOff';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import UndoIcon from '@mui/icons-material/Undo';
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useTranslation } from 'react-i18next';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

import { NAV_BG, AMBER, AMBER_DARK, AMBER_GLOW } from '../theme';
import { MapContainer, TileLayer, Marker, Polyline, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import iconUrl from 'leaflet/dist/images/marker-icon.png';
import iconShadowUrl from 'leaflet/dist/images/marker-shadow.png';
import { localDateString } from '../utils/date';

L.Marker.prototype.options.icon = L.icon({ iconUrl, shadowUrl: iconShadowUrl, iconSize: [25, 41], iconAnchor: [12, 41] });

const ROUTE_TYPE_COLORS = { 0: '#2563eb', 1: '#16a34a', 2: '#9333ea' };
const ROUTE_TYPE_ICONS = {
    0: <SchoolIcon sx={{ fontSize: 16 }} />,
    1: <HomeIcon sx={{ fontSize: 16 }} />,
    2: <EventIcon sx={{ fontSize: 16 }} />,
};

/* ── Read-only route preview map (OpenStreetMap/Leaflet) ─────── */
function FitBounds({ positions }) {
    const map = useMap();
    useEffect(() => {
        if (positions.length > 0) map.fitBounds(L.latLngBounds(positions), { padding: [32, 32] });
    }, [positions, map]);
    return null;
}

function RoutePreviewMap({ stops, schoolLat, schoolLng, routeType, depotLat, depotLng, onPathLoad }) {
    const { t } = useTranslation();
    const [routePath, setRoutePath] = useState(null);
    const [loadingPath, setLoadingPath] = useState(false);

    const center = schoolLat && schoolLng ? [parseFloat(schoolLat), parseFloat(schoolLng)] : [40, 22];
    const schoolIcon = L.divIcon({
        html: `<div style="width:26px;height:26px;border-radius:50%;background:#ef4444;border:2px solid #fff;display:flex;align-items:center;justify-content:center;color:#fff;font-weight:700;font-size:11px;box-shadow:0 2px 6px rgba(0,0,0,0.3)">S</div>`,
        className: '', iconSize: [26, 26], iconAnchor: [13, 13],
    });
    const positions = [center, ...stops.filter(s => s.latitude && s.longitude).map(s => [parseFloat(s.latitude), parseFloat(s.longitude)])];

    const toggleRoadPath = async () => {
        if (routePath) {
            setRoutePath(null);
            onPathLoad?.(null);
            return;
        }
        const validStops = stops.filter(s => s.latitude && s.longitude);
        if (validStops.length === 0) return;
        // Build coordinates; for types 0 and 1, prepend starting point so leg[i] = arrival at stop[i]
        let coords;
        if (routeType === 1 && schoolLat && schoolLng) {
            coords = [[parseFloat(schoolLng), parseFloat(schoolLat)], ...validStops.map(s => [parseFloat(s.longitude), parseFloat(s.latitude)])];
        } else if (routeType === 0 && schoolLat && schoolLng) {
            const hasDepot = depotLat && depotLng;
            const startLng = hasDepot ? parseFloat(depotLng) : parseFloat(schoolLng);
            const startLat = hasDepot ? parseFloat(depotLat) : parseFloat(schoolLat);
            coords = [[startLng, startLat], ...validStops.map(s => [parseFloat(s.longitude), parseFloat(s.latitude)]), [parseFloat(schoolLng), parseFloat(schoolLat)]];
        } else if (schoolLat && schoolLng) {
            coords = [...validStops.map(s => [parseFloat(s.longitude), parseFloat(s.latitude)]), [parseFloat(schoolLng), parseFloat(schoolLat)]];
        } else {
            coords = validStops.map(s => [parseFloat(s.longitude), parseFloat(s.latitude)]);
        }
        if (coords.length < 2) return;
        setLoadingPath(true);
        try {
            const coordStr = coords.map(([lng, lat]) => `${lng},${lat}`).join(';');
            const res = await fetch(`https://router.project-osrm.org/route/v1/driving/${coordStr}?overview=full&geometries=geojson`);
            const data = await res.json();
            if (data.code === 'Ok' && data.routes?.[0]?.geometry?.coordinates) {
                const route = data.routes[0];
                setRoutePath(route.geometry.coordinates.map(([lng, lat]) => [lat, lng]));
                onPathLoad?.({ summary: { distance: route.distance, duration: route.duration }, legs: route.legs });
            }
        } catch (e) {
            console.error('OSRM route fetch failed', e);
        } finally {
            setLoadingPath(false);
        }
    };

    return (
        <Box>
            <MapContainer center={center} zoom={13} style={{ height: 350, width: '100%', borderRadius: 12, border: '1px solid #e2e8f0' }} zoomControl={false} attributionControl={false}>
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                <FitBounds positions={positions} />
                <Marker position={center} icon={schoolIcon} />
                {stops.filter(s => s.latitude && s.longitude).map((stop, idx) => {
                    const icon = L.divIcon({
                        html: `<div style="width:26px;height:26px;border-radius:50%;background:#2563eb;border:2px solid #fff;display:flex;align-items:center;justify-content:center;color:#fff;font-weight:700;font-size:10px;box-shadow:0 2px 6px rgba(0,0,0,0.25)">${idx + 1}</div>`,
                        className: '', iconSize: [26, 26], iconAnchor: [13, 13],
                    });
                    return <Marker key={stop.routepoint_id} position={[parseFloat(stop.latitude), parseFloat(stop.longitude)]} icon={icon} />;
                })}
                {routePath && <Polyline positions={routePath} color="#2563eb" weight={4} opacity={0.75} />}
            </MapContainer>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                <Button
                    size="small"
                    variant={routePath ? 'contained' : 'outlined'}
                    onClick={toggleRoadPath}
                    disabled={loadingPath}
                    startIcon={loadingPath ? <CircularProgress size={12} sx={{ color: 'inherit' }} /> : <RouteIcon sx={{ fontSize: 14 }} />}
                    sx={{
                        fontFamily: '"Outfit", sans-serif', fontWeight: 600, fontSize: '0.72rem',
                        textTransform: 'none', borderRadius: '8px', py: 0.5, px: 1.5,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        ...(routePath
                            ? { bgcolor: '#2563eb', color: '#fff', '&:hover': { bgcolor: '#1d4ed8' } }
                            : { borderColor: '#2563eb', color: '#2563eb', '&:hover': { bgcolor: '#eff6ff' } }
                        ),
                    }}
                >
                    {loadingPath ? t('routes.road_path_loading') : routePath ? t('routes.hide_road_path') : t('routes.show_road_path')}
                </Button>
            </Box>
        </Box>
    );
}

/* ── Editor Map component ────────────────────────────────────── */
function EditorMap({ schoolLat, schoolLng, availableChildren, selectedStops, onTogglePoi, depotLat, depotLng, pickingDepot, onMapClick, routePath }) {
    const containerRef = useRef(null);
    const mapRef = useRef(null);
    const markersRef = useRef([]);
    const polylineRef = useRef(null);
    const clickListenerRef = useRef(null);
    const onMapClickRef = useRef(onMapClick);
    const pickingDepotRef = useRef(pickingDepot);
    onMapClickRef.current = onMapClick;
    pickingDepotRef.current = pickingDepot;

    const center = useMemo(() => ({ lat: parseFloat(schoolLat) || 37.998, lng: parseFloat(schoolLng) || 23.797 }), [schoolLat, schoolLng]);

    // Initialize map
    useEffect(() => {
        if (!containerRef.current || !window.google || mapRef.current) return;
        mapRef.current = new window.google.maps.Map(containerRef.current, {
            center,
            zoom: 13,
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: false,
            styles: [
                { featureType: 'poi', stylers: [{ visibility: 'off' }] },
                { featureType: 'transit', stylers: [{ visibility: 'off' }] },
            ],
        });
    });

    // Update markers whenever available children, selection, or filters change
    useEffect(() => {
        if (!mapRef.current || !window.google) return;

        // Attach map click listener once
        if (!clickListenerRef.current) {
            clickListenerRef.current = mapRef.current.addListener('click', (e) => {
                if (pickingDepotRef.current && onMapClickRef.current) {
                    onMapClickRef.current(e.latLng.lat(), e.latLng.lng());
                }
            });
        }

        // Clear old markers
        markersRef.current.forEach(m => m.setMap(null));
        markersRef.current = [];

        const selectedPoiIds = new Set(selectedStops.map(s => s.poi_id));

        // Depot marker (pickup routes only)
        if (depotLat && depotLng) {
            const depotMarker = new window.google.maps.Marker({
                position: { lat: parseFloat(depotLat), lng: parseFloat(depotLng) },
                map: mapRef.current,
                icon: {
                    path: window.google.maps.SymbolPath.CIRCLE,
                    scale: 13,
                    fillColor: '#F59E0B',
                    fillOpacity: 1,
                    strokeColor: '#fff',
                    strokeWeight: 2,
                },
                label: { text: 'D', color: '#fff', fontWeight: 'bold', fontSize: '11px' },
                title: 'Bus starting point',
                zIndex: 90,
            });
            markersRef.current.push(depotMarker);
        }

        // School marker
        const schoolMarker = new window.google.maps.Marker({
            position: center,
            map: mapRef.current,
            icon: {
                path: window.google.maps.SymbolPath.CIRCLE,
                scale: 12,
                fillColor: '#ef4444',
                fillOpacity: 1,
                strokeColor: '#fff',
                strokeWeight: 2,
            },
            title: 'School',
            label: { text: 'S', color: '#fff', fontWeight: 'bold', fontSize: '11px' },
            zIndex: 100,
        });
        markersRef.current.push(schoolMarker);

        // Available (filtered) but NOT yet selected → blue markers
        availableChildren.forEach(child => {
            (child.pois || []).forEach(poi => {
                if (selectedPoiIds.has(poi.poi_id)) return;
                const marker = new window.google.maps.Marker({
                    position: { lat: parseFloat(poi.latitude), lng: parseFloat(poi.longitude) },
                    map: mapRef.current,
                    icon: {
                        path: window.google.maps.SymbolPath.CIRCLE,
                        scale: 10,
                        fillColor: '#3b82f6',
                        fillOpacity: 0.9,
                        strokeColor: '#fff',
                        strokeWeight: 2,
                    },
                    title: `${child.firstname} ${child.lastname}\n${poi.address || poi.name}`,
                    zIndex: 30,
                    cursor: 'pointer',
                });
                marker.addListener('click', () => onTogglePoi(poi, child));
                markersRef.current.push(marker);
            });
        });

        // Selected stop markers → green with order number, clickable to remove
        selectedStops.forEach((stop, idx) => {
            const marker = new window.google.maps.Marker({
                position: { lat: parseFloat(stop.latitude), lng: parseFloat(stop.longitude) },
                map: mapRef.current,
                icon: {
                    path: window.google.maps.SymbolPath.CIRCLE,
                    scale: 14,
                    fillColor: '#16a34a',
                    fillOpacity: 1,
                    strokeColor: '#fff',
                    strokeWeight: 2,
                },
                label: { text: String(idx + 1), color: '#fff', fontWeight: 'bold', fontSize: '11px' },
                title: `${stop.child_firstname} ${stop.child_lastname}\n${stop.address || stop.name}`,
                zIndex: 50,
                cursor: 'pointer',
            });
            marker.addListener('click', () =>
                onTogglePoi(
                    { poi_id: stop.poi_id, address: stop.address, latitude: stop.latitude, longitude: stop.longitude, name: stop.name },
                    { child_id: stop.child_id, firstname: stop.child_firstname, lastname: stop.child_lastname },
                )
            );
            markersRef.current.push(marker);
        });

        // Fit map to all visible markers
        const bounds = new window.google.maps.LatLngBounds();
        bounds.extend(center);
        if (depotLat && depotLng) bounds.extend({ lat: parseFloat(depotLat), lng: parseFloat(depotLng) });
        availableChildren.forEach(child => {
            (child.pois || []).forEach(poi => {
                if (!selectedPoiIds.has(poi.poi_id)) {
                    bounds.extend({ lat: parseFloat(poi.latitude), lng: parseFloat(poi.longitude) });
                }
            });
        });
        selectedStops.forEach(stop => {
            bounds.extend({ lat: parseFloat(stop.latitude), lng: parseFloat(stop.longitude) });
        });
        mapRef.current.fitBounds(bounds, { top: 40, right: 40, bottom: 40, left: 40 });

        // cleanup
        return () => {
            markersRef.current.forEach(m => m.setMap(null));
            markersRef.current = [];
        };
    }, [availableChildren, selectedStops, center, onTogglePoi, depotLat, depotLng]);

    // Change cursor when in depot-picking mode
    useEffect(() => {
        if (!mapRef.current) return;
        mapRef.current.setOptions({ draggableCursor: pickingDepot ? 'crosshair' : null });
    }, [pickingDepot]);

    // Draw/update route polyline
    useEffect(() => {
        if (!window.google) return;
        if (polylineRef.current) {
            polylineRef.current.setMap(null);
            polylineRef.current = null;
        }
        if (!mapRef.current || !routePath || routePath.length < 2) return;
        polylineRef.current = new window.google.maps.Polyline({
            path: routePath,
            map: mapRef.current,
            strokeColor: '#2563eb',
            strokeOpacity: 0.8,
            strokeWeight: 4,
        });
        return () => {
            if (polylineRef.current) {
                polylineRef.current.setMap(null);
                polylineRef.current = null;
            }
        };
    }, [routePath]);

    return (
        <div
            ref={containerRef}
            style={{ width: '100%', height: 420, borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden' }}
        />
    );
}

const formatDistance = (meters) => {
    if (meters >= 1000) return `${(meters / 1000).toFixed(1)} km`;
    return `${Math.round(meters)} m`;
};
const formatDuration = (seconds) => {
    const m = Math.round(seconds / 60);
    if (m >= 60) return `${Math.floor(m / 60)}h ${m % 60}m`;
    return `${m} min`;
};
// Returns the OSRM leg arriving at stop `idx`.
// route_type 1 (from school): school is coord[0], so leg[i] arrives at stop[i].
// route_type 0 (to school): school/depot is coord[0], stops follow, so leg[i] arrives at stop[i].
// route_type 2 (one-time): no start coord prepended, so leg[i-1] arrives at stop[i].
const getLegForStop = (idx, routeType, legs) => {
    if (!legs?.length) return null;
    return (routeType === 0 || routeType === 1) ? (legs[idx] ?? null) : (idx > 0 ? legs[idx - 1] ?? null : null);
};

// Returns estimated arrival time string (HH:MM) at stop `idx`.
// Assumes legs[0..idx] are available (start-point-inclusive coord scheme for types 0 and 1).
const getArrivalTime = (startTime, stopIdx, legs) => {
    if (!startTime || !legs?.length) return null;
    const [h, m] = startTime.split(':').map(Number);
    let totalSeconds = h * 3600 + m * 60;
    for (let i = 0; i <= stopIdx; i++) {
        if (!legs[i]) return null;
        totalSeconds += legs[i].duration;
        if (i < stopIdx) totalSeconds += 3; // 3-second boarding delay per prior stop
    }
    const hh = Math.floor(totalSeconds / 3600) % 24;
    const mm = Math.floor((totalSeconds % 3600) / 60);
    return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
};

/* ── Draggable stop item ─────────────────────────────────────── */
const DRAG_TYPE = 'STOP_ITEM';

function DraggableStop({ stop, idx, total, onMove, onRemove }) {
    const ref = useRef(null);

    const [, drop] = useDrop({
        accept: DRAG_TYPE,
        hover(item) {
            if (item.index === idx) return;
            onMove(item.index, idx);
            item.index = idx;
        },
    });

    const [{ isDragging }, drag, preview] = useDrag({
        type: DRAG_TYPE,
        item: { index: idx },
        collect: monitor => ({ isDragging: monitor.isDragging() }),
    });

    drag(drop(ref));

    return (
        <Box
            ref={ref}
            sx={{
                display: 'flex', alignItems: 'center', gap: 0.5,
                p: 1, borderRadius: '10px', bgcolor: '#f8fafc', border: '1px solid #e2e8f0',
                opacity: isDragging ? 0.4 : 1,
                cursor: 'grab',
                '&:active': { cursor: 'grabbing' },
            }}
        >
            <DragIndicatorIcon sx={{ fontSize: 16, color: '#94a3b8', flexShrink: 0 }} />
            <Box sx={{
                width: 26, height: 26, borderRadius: '7px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                backgroundColor: '#16a34a', color: '#fff', fontSize: '0.72rem', fontWeight: 700, flexShrink: 0,
                fontFamily: '"Outfit", sans-serif',
            }}>
                {idx + 1}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 600, fontSize: '0.78rem', color: NAV_BG, lineHeight: 1.3 }}>
                    {stop.child_firstname} {stop.child_lastname}
                </Typography>
                <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.68rem', color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {stop.address || stop.name}
                </Typography>
            </Box>
            <IconButton size="small" onClick={() => onRemove(idx)} sx={{ p: 0.5 }}>
                <CloseIcon sx={{ fontSize: 14, color: '#ef4444' }} />
            </IconButton>
        </Box>
    );
}

/* ── Main component ──────────────────────────────────────────── */
const RoutesPage = () => {
    const { authTokens, logoutUser, backendUrl, profile, pageTitle } = useContext(AuthContext);
    const { t } = useTranslation();

    const [routes, setRoutes] = useState([]);
    const [busChildren, setBusChildren] = useState([]);
    const [pendingChildren, setPendingChildren] = useState([]);
    const [classes, setClasses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState(0);
    const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });

    const [editorOpen, setEditorOpen] = useState(false);
    const [editingRoute, setEditingRoute] = useState(null);
    const [routeName, setRouteName] = useState('');
    const [routeType, setRouteType] = useState(0);
    const [selectedStops, setSelectedStops] = useState([]);

    const [filterClass, setFilterClass] = useState('');
    const [filterDepartment, setFilterDepartment] = useState('');

    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleteRoute, setDeleteRouteState] = useState(null);

    const [expandedRouteId, setExpandedRouteId] = useState(null);
    const [roadData, setRoadData] = useState(null); // { routeId, summary: {distance, duration}, legs: [] }

    useEffect(() => { setRoadData(null); }, [expandedRouteId]);

    const [optimizing, setOptimizing] = useState(false);
    const [preOptimizeStops, setPreOptimizeStops] = useState(null); // snapshot for undo

    const [editorRoutePath, setEditorRoutePath] = useState(null);   // [{lat,lng}] | null
    const [editorPathData, setEditorPathData] = useState(null);     // {summary, legs} | null
    const [editorLoadingPath, setEditorLoadingPath] = useState(false);

    const [depotLat, setDepotLat] = useState(null);
    const [depotLng, setDepotLng] = useState(null);
    const [depotAddress, setDepotAddress] = useState('');
    const [pickingDepot, setPickingDepot] = useState(false);
    const [routeStartTime, setRouteStartTime] = useState('');

    // Absence management
    const [absenceDialogOpen, setAbsenceDialogOpen] = useState(false);
    const [absenceChild, setAbsenceChild] = useState(null); // { child_id, name }
    const [absenceList, setAbsenceList] = useState([]);
    const [absenceDate, setAbsenceDate] = useState('');
    const [absenceReason, setAbsenceReason] = useState('');
    const [absenceSaving, setAbsenceSaving] = useState(false);
    const [todayAbsentChildIds, setTodayAbsentChildIds] = useState(new Set());

    document.title = (pageTitle || '') + ' - ' + t('routes.title');

    const headers = useMemo(() => ({
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + String(authTokens?.access),
    }), [authTokens]);

    const fetchRoutes = useCallback(async () => {
        try {
            const res = await fetch(backendUrl + 'api/get-routes/', { headers });
            if (res.status === 401) { logoutUser(); return; }
            setRoutes(await res.json());
        } catch (e) { console.error(e); }
    }, [backendUrl, headers, logoutUser]);

    const fetchBusChildren = useCallback(async () => {
        try {
            const res = await fetch(backendUrl + 'api/bus-children/', { headers });
            if (res.status === 401) { logoutUser(); return; }
            setBusChildren(await res.json());
        } catch (e) { console.error(e); }
    }, [backendUrl, headers, logoutUser]);

    const fetchPendingChildren = useCallback(async () => {
        try {
            const res = await fetch(backendUrl + 'api/pending-bus-children/', { headers });
            if (res.status === 401) { logoutUser(); return; }
            setPendingChildren(await res.json());
        } catch (e) { console.error(e); }
    }, [backendUrl, headers, logoutUser]);

    const fetchClasses = useCallback(async () => {
        try {
            const res = await fetch(backendUrl + 'api/get-classes/', { headers });
            if (res.status === 401) { logoutUser(); return; }
            setClasses(await res.json());
        } catch (e) { console.error(e); }
    }, [backendUrl, headers, logoutUser]);

    const fetchTodayAbsences = useCallback(async () => {
        try {
            const res = await fetch(backendUrl + 'api/absences-for-date/', { headers });
            if (res.ok) {
                const data = await res.json();
                setTodayAbsentChildIds(new Set(data));
            }
        } catch (e) { console.error(e); }
    }, [backendUrl, headers]);

    const fetchAbsencesForChild = useCallback(async (childId) => {
        try {
            const res = await fetch(backendUrl + `api/child-absences/?child_id=${childId}`, { headers });
            if (res.ok) setAbsenceList(await res.json());
        } catch (e) { console.error(e); }
    }, [backendUrl, headers]);

    const openAbsenceDialog = (stop) => {
        setAbsenceChild({ child_id: stop.child_id, name: `${stop.child_firstname} ${stop.child_lastname}` });
        setAbsenceDate('');
        setAbsenceReason('');
        setAbsenceList([]);
        setAbsenceDialogOpen(true);
        fetchAbsencesForChild(stop.child_id);
    };

    const handleAddAbsence = async () => {
        if (!absenceDate) return;
        setAbsenceSaving(true);
        try {
            const res = await fetch(backendUrl + 'api/child-absences/', {
                method: 'POST', headers,
                body: JSON.stringify({ child_id: absenceChild.child_id, date: absenceDate, reason: absenceReason }),
            });
            if (res.ok) {
                setSnack({ open: true, message: t('absences.added'), severity: 'success' });
                setAbsenceDate('');
                setAbsenceReason('');
                fetchAbsencesForChild(absenceChild.child_id);
                fetchTodayAbsences();
            } else {
                setSnack({ open: true, message: t('absences.error'), severity: 'error' });
            }
        } catch (e) {
            setSnack({ open: true, message: t('absences.error'), severity: 'error' });
        } finally {
            setAbsenceSaving(false);
        }
    };

    const handleDeleteAbsence = async (absenceId) => {
        try {
            const res = await fetch(backendUrl + `api/child-absence/${absenceId}/`, { method: 'DELETE', headers });
            if (res.ok) {
                setSnack({ open: true, message: t('absences.removed'), severity: 'success' });
                fetchAbsencesForChild(absenceChild.child_id);
                fetchTodayAbsences();
            }
        } catch (e) { console.error(e); }
    };

    useEffect(() => {
        Promise.all([fetchRoutes(), fetchBusChildren(), fetchPendingChildren(), fetchClasses(), fetchTodayAbsences()]).then(() => setLoading(false));
    }, []);

    /* ── Children already in a route of the same type ──────────── */
    const childrenInRouteType = useMemo(() => {
        const set = new Set();
        routes.forEach(r => {
            if (r.route_type === routeType && (!editingRoute || r.id !== editingRoute.id)) {
                r.stops.forEach(s => set.add(s.child_id));
            }
        });
        return set;
    }, [routes, routeType, editingRoute]);

    /* ── All bus children for the map (unfiltered) ───────────── */
    const mapChildren = useMemo(() => {
        let filtered = busChildren;
        if (routeType === 0) filtered = filtered.filter(c => c.bus_to_school);
        else if (routeType === 1) filtered = filtered.filter(c => c.bus_from_school);
        if (routeType !== 2) filtered = filtered.filter(c => !childrenInRouteType.has(c.child_id));
        return filtered;
    }, [busChildren, routeType, childrenInRouteType]);

    /* ── Filtered children for the side list ─────────────────── */
    const availableChildren = useMemo(() => {
        let filtered = mapChildren;
        if (filterClass) filtered = filtered.filter(c => String(c.schoolclass_id) === String(filterClass));
        if (filterDepartment) filtered = filtered.filter(c => String(c.department_id) === String(filterDepartment));
        return filtered;
    }, [mapChildren, filterClass, filterDepartment]);

    const departmentsForClass = useMemo(() => {
        if (!filterClass) return [];
        const cls = classes.find(c => String(c.id) === String(filterClass));
        return cls ? cls.departments : [];
    }, [classes, filterClass]);

    const selectedPoiIds = useMemo(() => new Set(selectedStops.map(s => s.poi_id)), [selectedStops]);

    const openNewRoute = () => {
        setEditingRoute(null);
        setRouteName('');
        setRouteType(0);
        setSelectedStops([]);
        setFilterClass('');
        setFilterDepartment('');
        setPreOptimizeStops(null);
        setDepotLat(null);
        setDepotLng(null);
        setDepotAddress('');
        setPickingDepot(false);
        setEditorRoutePath(null);
        setEditorPathData(null);
        setRouteStartTime('');
        setEditorOpen(true);
    };

    const openEditRoute = (route) => {
        setEditingRoute(route);
        setRouteName(route.name);
        setRouteType(route.route_type);
        setPreOptimizeStops(null);
        setEditorRoutePath(null);
        setEditorPathData(null);
        setSelectedStops(route.stops.map(s => ({
            poi_id: s.poi_id, child_id: s.child_id,
            child_firstname: s.child_firstname, child_lastname: s.child_lastname,
            address: s.address, latitude: s.latitude, longitude: s.longitude, name: s.name,
        })));
        setDepotLat(route.depot_latitude != null ? String(route.depot_latitude) : null);
        setDepotLng(route.depot_longitude != null ? String(route.depot_longitude) : null);
        setDepotAddress(route.depot_address || '');
        setPickingDepot(false);
        setRouteStartTime(route.start_time || '');
        setFilterClass('');
        setFilterDepartment('');
        setEditorOpen(true);
    };

    const togglePoi = useCallback((poi, child) => {
        setSelectedStops(prev => {
            const exists = prev.find(s => s.poi_id === poi.poi_id);
            if (exists) return prev.filter(s => s.poi_id !== poi.poi_id);
            return [...prev, {
                poi_id: poi.poi_id, child_id: child.child_id,
                child_firstname: child.firstname, child_lastname: child.lastname,
                address: poi.address, latitude: poi.latitude, longitude: poi.longitude, name: poi.name,
            }];
        });
    }, []);

    const reorderStop = useCallback((fromIdx, toIdx) => {
        setSelectedStops(prev => {
            const next = [...prev];
            const [item] = next.splice(fromIdx, 1);
            next.splice(toIdx, 0, item);
            return next;
        });
    }, []);

    const removeStop = (index) => setSelectedStops(prev => prev.filter((_, i) => i !== index));

    const handleMapClick = useCallback((lat, lng) => {
        setDepotLat(String(lat));
        setDepotLng(String(lng));
        setPickingDepot(false);
        if (window.google && window.google.maps) {
            const geocoder = new window.google.maps.Geocoder();
            geocoder.geocode({ location: { lat, lng } }, (results, status) => {
                setDepotAddress(
                    status === 'OK' && results[0]
                        ? results[0].formatted_address
                        : `${lat.toFixed(5)}, ${lng.toFixed(5)}`
                );
            });
        } else {
            setDepotAddress(`${lat.toFixed(5)}, ${lng.toFixed(5)}`);
        }
    }, []);

    const fetchEditorPath = async (stops) => {
        const validStops = stops.filter(s => s.latitude && s.longitude);
        if (validStops.length < 2) return;
        const schoolLat = profile?.schoollatitude;
        const schoolLng = profile?.schoollongitude;
        let coords;
        if (routeType === 1 && schoolLat && schoolLng) {
            coords = [[parseFloat(schoolLng), parseFloat(schoolLat)], ...validStops.map(s => [parseFloat(s.longitude), parseFloat(s.latitude)])];
        } else if (routeType === 0 && schoolLat && schoolLng) {
            // Prepend starting point (depot or school) so leg[i] = arrival at stop[i]
            const hasDepot = depotLat && depotLng;
            const startLng = hasDepot ? parseFloat(depotLng) : parseFloat(schoolLng);
            const startLat = hasDepot ? parseFloat(depotLat) : parseFloat(schoolLat);
            coords = [[startLng, startLat], ...validStops.map(s => [parseFloat(s.longitude), parseFloat(s.latitude)]), [parseFloat(schoolLng), parseFloat(schoolLat)]];
        } else if (schoolLat && schoolLng) {
            coords = [...validStops.map(s => [parseFloat(s.longitude), parseFloat(s.latitude)]), [parseFloat(schoolLng), parseFloat(schoolLat)]];
        } else {
            coords = validStops.map(s => [parseFloat(s.longitude), parseFloat(s.latitude)]);
        }
        if (coords.length < 2) return;
        setEditorLoadingPath(true);
        try {
            const coordStr = coords.map(([lng, lat]) => `${lng},${lat}`).join(';');
            const res = await fetch(`https://router.project-osrm.org/route/v1/driving/${coordStr}?overview=full&geometries=geojson`);
            const data = await res.json();
            if (data.code === 'Ok' && data.routes?.[0]?.geometry?.coordinates) {
                const route = data.routes[0];
                setEditorRoutePath(route.geometry.coordinates.map(([lng, lat]) => ({ lat, lng })));
                setEditorPathData({ summary: { distance: route.distance, duration: route.duration }, legs: route.legs });
            }
        } catch (e) {
            console.error('OSRM route fetch failed', e);
        } finally {
            setEditorLoadingPath(false);
        }
    };

    const toggleEditorPath = async () => {
        if (editorRoutePath) {
            setEditorRoutePath(null);
            setEditorPathData(null);
            return;
        }
        await fetchEditorPath(selectedStops);
    };

    const handleOptimize = async () => {
        if (selectedStops.length < 2) {
            setSnack({ open: true, message: t('routes.optimize_too_few'), severity: 'info' });
            return;
        }
        setOptimizing(true);
        try {
            const stopsPayload = selectedStops
                .filter(s => s.latitude && s.longitude)
                .map(s => ({ poi_id: s.poi_id, latitude: s.latitude, longitude: s.longitude }));
            const res = await fetch(backendUrl + 'api/optimize-route/', {
                method: 'POST', headers,
                body: JSON.stringify({
                    stops: stopsPayload,
                    route_type: routeType,
                    school_lat: profile?.schoollatitude,
                    school_lng: profile?.schoollongitude,
                    ...(routeType === 0 && depotLat && depotLng ? { depot_lat: depotLat, depot_lng: depotLng } : {}),
                }),
            });
            if (!res.ok) {
                setSnack({ open: true, message: t('routes.optimize_error'), severity: 'error' });
                return;
            }
            const { ordered_poi_ids } = await res.json();
            const poiMap = Object.fromEntries(selectedStops.map(s => [s.poi_id, s]));
            const reordered = ordered_poi_ids.map(id => poiMap[id]).filter(Boolean);
            // Keep any stops that had no coordinates at the end
            const noCoord = selectedStops.filter(s => !s.latitude || !s.longitude);
            setPreOptimizeStops([...selectedStops]);
            setSelectedStops([...reordered, ...noCoord]);
            setSnack({ open: true, message: t('routes.optimize_applied'), severity: 'success' });
            if (editorRoutePath) {
                fetchEditorPath([...reordered, ...noCoord]);
            }
        } catch {
            setSnack({ open: true, message: t('routes.optimize_error'), severity: 'error' });
        } finally {
            setOptimizing(false);
        }
    };

    const handleSaveRoute = async () => {
        try {
            const res = await fetch(backendUrl + 'api/save-route/', {
                method: 'POST', headers,
                body: JSON.stringify({
                    route_id: editingRoute?.id || null,
                    name: routeName, route_type: routeType,
                    poi_ids: selectedStops.map(s => s.poi_id),
                    depot_latitude: routeType === 0 ? depotLat : null,
                    depot_longitude: routeType === 0 ? depotLng : null,
                    depot_address: routeType === 0 ? (depotAddress || null) : null,
                    start_time: routeStartTime || null,
                }),
            });
            if (res.status === 401) { logoutUser(); return; }
            if (res.ok) {
                setSnack({ open: true, message: editingRoute ? t('routes.updated') : t('routes.created'), severity: 'success' });
                setEditorOpen(false);
                fetchRoutes();
                fetchBusChildren();
                fetchPendingChildren();
            } else {
                const err = await res.json();
                setSnack({ open: true, message: err.error || t('routes.save_failed'), severity: 'error' });
            }
        } catch (e) {
            setSnack({ open: true, message: t('routes.save_failed'), severity: 'error' });
        }
    };

    const handleDelete = async () => {
        try {
            const res = await fetch(backendUrl + `api/delete-route/${deleteRoute.id}/`, { method: 'DELETE', headers });
            if (res.ok) {
                setSnack({ open: true, message: t('routes.deleted'), severity: 'success' });
                setDeleteOpen(false);
                fetchRoutes();
                fetchBusChildren();
                fetchPendingChildren();
            }
        } catch (e) {
            setSnack({ open: true, message: t('routes.save_failed'), severity: 'error' });
        }
    };

    const routeTypeLabel = (type) => [t('routes.to_school'), t('routes.from_school'), t('routes.one_time')][type] || '';

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
                <Box sx={{ textAlign: 'center' }}>
                    <CircularProgress size={44} thickness={3} sx={{ color: AMBER }} />
                    <Typography sx={{ mt: 2, fontFamily: '"Outfit", sans-serif', fontSize: '0.875rem', color: '#64748b' }}>
                        {t('routes.loading')}
                    </Typography>
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{ minHeight: '100vh', background: '#f1f5f9', pt: 4, pb: 6 }}>
            <Container maxWidth="xl">

                {/* ── Page Header ── */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            width: 42, height: 42, borderRadius: '12px',
                            background: `linear-gradient(135deg, ${NAV_BG} 0%, #163571 100%)`,
                            boxShadow: '0 4px 14px rgba(15,45,94,0.25)', flexShrink: 0,
                        }}>
                            <RouteIcon sx={{ color: AMBER, fontSize: 22 }} />
                        </Box>
                        <Box>
                            <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, fontSize: '1.5rem', color: NAV_BG, lineHeight: 1.2 }}>
                                {t('routes.title')}
                            </Typography>
                            <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.8rem', color: '#94a3b8', fontWeight: 400, mt: 0.1 }}>
                                {t('routes.subtitle')}
                            </Typography>
                        </Box>
                        <Chip
                            label={routes.length} size="small"
                            sx={{
                                ml: 0.5, fontFamily: '"Outfit", sans-serif', fontWeight: 700, fontSize: '0.75rem',
                                background: `linear-gradient(135deg, ${AMBER} 0%, ${AMBER_DARK} 100%)`,
                                color: NAV_BG, boxShadow: `0 2px 8px ${AMBER_GLOW}`, border: 'none', height: 24,
                                alignSelf: 'flex-start', mt: 0.25,
                            }}
                        />
                    </Box>
                    <Button
                        variant="contained" startIcon={<AddIcon />} onClick={openNewRoute}
                        sx={{
                            fontFamily: '"Outfit", sans-serif', fontWeight: 600, fontSize: '0.875rem', textTransform: 'none',
                            borderRadius: '10px', px: 2.5, py: 1,
                            background: `linear-gradient(135deg, ${AMBER} 0%, ${AMBER_DARK} 100%)`,
                            color: NAV_BG, boxShadow: `0 4px 14px ${AMBER_GLOW}`, border: 'none',
                            '&:hover': { background: `linear-gradient(135deg, #fbbf24 0%, ${AMBER} 100%)`, transform: 'translateY(-1px)' },
                        }}
                    >
                        {t('routes.add')}
                    </Button>
                </Box>

                {/* ── Tabs ── */}
                <Box sx={{ mb: 3 }}>
                    <Tabs
                        value={activeTab}
                        onChange={(_, v) => setActiveTab(v)}
                        sx={{
                            '& .MuiTabs-indicator': { backgroundColor: AMBER },
                            '& .MuiTab-root': { fontFamily: '"Outfit", sans-serif', fontWeight: 600, fontSize: '0.875rem', textTransform: 'none', color: '#64748b', minHeight: 44 },
                            '& .Mui-selected': { color: NAV_BG },
                        }}
                    >
                        <Tab
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {t('routes.tab_routes')}
                                    <Chip label={routes.length} size="small" sx={{ height: 20, fontSize: '0.7rem', fontFamily: '"Outfit", sans-serif', fontWeight: 700, background: activeTab === 0 ? `linear-gradient(135deg, ${AMBER} 0%, ${AMBER_DARK} 100%)` : '#e2e8f0', color: activeTab === 0 ? NAV_BG : '#64748b', border: 'none' }} />
                                </Box>
                            }
                        />
                        <Tab
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {t('routes.tab_pending')}
                                    {pendingChildren.length > 0 && (
                                        <Chip label={pendingChildren.length} size="small" sx={{ height: 20, fontSize: '0.7rem', fontFamily: '"Outfit", sans-serif', fontWeight: 700, background: activeTab === 1 ? '#f97316' : 'rgba(249,115,22,0.15)', color: activeTab === 1 ? '#fff' : '#c2410c', border: 'none' }} />
                                    )}
                                </Box>
                            }
                        />
                    </Tabs>
                    <Divider />
                </Box>

                {/* ── Pending Children Tab ── */}
                {activeTab === 1 && (
                    pendingChildren.length === 0 ? (
                        <Paper sx={{ p: 6, textAlign: 'center', borderRadius: '16px' }}>
                            <RouteIcon sx={{ fontSize: 48, color: '#86efac', mb: 2 }} />
                            <Typography sx={{ fontFamily: '"Outfit", sans-serif', color: '#15803d', fontWeight: 600 }}>
                                {t('routes.pending_empty')}
                            </Typography>
                        </Paper>
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            {pendingChildren.map(child => (
                                <Paper key={child.child_id} sx={{ borderRadius: '14px', overflow: 'hidden', border: '1px solid #fed7aa', '&:hover': { boxShadow: '0 4px 16px rgba(249,115,22,0.1)' } }}>
                                    <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                                        <Box sx={{
                                            width: 36, height: 36, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            background: 'rgba(249,115,22,0.12)', color: '#f97316', flexShrink: 0,
                                        }}>
                                            <WarningAmberIcon sx={{ fontSize: 18 }} />
                                        </Box>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 600, fontSize: '0.95rem', color: NAV_BG }}>
                                                {child.firstname} {child.lastname}
                                            </Typography>
                                            <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.75rem', color: '#64748b', mt: 0.25 }}>
                                                {child.schoolclass_name}{child.department_name ? ` · ${child.department_name}` : ''}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', flexWrap: 'wrap' }}>
                                            {child.pending_to_school && (
                                                <Chip
                                                    icon={<SchoolIcon sx={{ fontSize: '0.85rem !important' }} />}
                                                    label={t('routes.pending_to_school')}
                                                    size="small"
                                                    sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.72rem', height: 24, background: 'rgba(37,99,235,0.1)', color: '#1d4ed8', border: 'none' }}
                                                />
                                            )}
                                            {child.pending_from_school && (
                                                <Chip
                                                    icon={<HomeIcon sx={{ fontSize: '0.85rem !important' }} />}
                                                    label={t('routes.pending_from_school')}
                                                    size="small"
                                                    sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.72rem', height: 24, background: 'rgba(22,163,74,0.1)', color: '#15803d', border: 'none' }}
                                                />
                                            )}
                                            {!child.has_address && (
                                                <Tooltip title={t('routes.no_address_warning')} arrow>
                                                    <Chip
                                                        icon={<LocationOffIcon sx={{ fontSize: '0.85rem !important' }} />}
                                                        label="No address"
                                                        size="small"
                                                        sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.72rem', height: 24, background: 'rgba(239,68,68,0.1)', color: '#b91c1c', border: 'none', cursor: 'help' }}
                                                    />
                                                </Tooltip>
                                            )}
                                        </Box>
                                    </Box>
                                </Paper>
                            ))}
                        </Box>
                    )
                )}

                {/* ── Routes List ── */}
                {activeTab === 0 && (routes.length === 0 ? (
                    <Paper sx={{ p: 6, textAlign: 'center', borderRadius: '16px' }}>
                        <RouteIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 2 }} />
                        <Typography sx={{ fontFamily: '"Outfit", sans-serif', color: '#64748b' }}>
                            {t('routes.no_routes')}
                        </Typography>
                    </Paper>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {routes.map(route => (
                            <Paper key={route.id} sx={{ borderRadius: '16px', overflow: 'hidden', transition: 'box-shadow 0.2s', '&:hover': { boxShadow: '0 4px 20px rgba(0,0,0,0.08)' } }}>
                                <Box
                                    sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 2, cursor: 'pointer' }}
                                    onClick={() => setExpandedRouteId(expandedRouteId === route.id ? null : route.id)}
                                >
                                    <Box sx={{
                                        width: 36, height: 36, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        background: ROUTE_TYPE_COLORS[route.route_type] + '18',
                                        color: ROUTE_TYPE_COLORS[route.route_type],
                                    }}>
                                        {ROUTE_TYPE_ICONS[route.route_type]}
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 600, fontSize: '1rem', color: NAV_BG }}>
                                            {route.name}
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                                            <Chip
                                                label={routeTypeLabel(route.route_type)} size="small"
                                                sx={{
                                                    fontSize: '0.7rem', height: 20,
                                                    backgroundColor: ROUTE_TYPE_COLORS[route.route_type] + '18',
                                                    color: ROUTE_TYPE_COLORS[route.route_type],
                                                    fontFamily: '"Outfit", sans-serif', fontWeight: 600,
                                                }}
                                            />
                                            <Chip
                                                label={`${route.stops.length} ${t('routes.stops')}`} size="small"
                                                sx={{ fontSize: '0.7rem', height: 20, backgroundColor: '#f1f5f9', color: '#64748b', fontFamily: '"Outfit", sans-serif' }}
                                            />
                                            {route.start_time && (
                                                <Chip
                                                    icon={<AccessTimeIcon sx={{ fontSize: '0.8rem !important' }} />}
                                                    label={route.start_time}
                                                    size="small"
                                                    sx={{ fontSize: '0.7rem', height: 20, backgroundColor: '#f0fdf4', color: '#15803d', fontFamily: '"Outfit", sans-serif', fontWeight: 600 }}
                                                />
                                            )}
                                        </Box>
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                        <Tooltip title={t('routes.edit')}>
                                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); openEditRoute(route); }}>
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title={t('routes.delete')}>
                                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); setDeleteRouteState(route); setDeleteOpen(true); }}>
                                                <DeleteIcon fontSize="small" sx={{ color: '#ef4444' }} />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </Box>

                                {/* Expanded detail */}
                                {expandedRouteId === route.id && (
                                    <Box sx={{ px: 2.5, pb: 2.5 }}>
                                        <Divider sx={{ mb: 2 }} />

                                        {/* Road path summary — shown when path is loaded */}
                                        {roadData?.routeId === route.id && (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, px: 1.5, py: 0.9, borderRadius: '10px', bgcolor: '#eff6ff', border: '1px solid #bfdbfe' }}>
                                                <RouteIcon sx={{ color: '#2563eb', fontSize: 16, flexShrink: 0 }} />
                                                <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, fontSize: '0.82rem', color: '#1d4ed8' }}>
                                                    {formatDistance(roadData.summary.distance)}
                                                </Typography>
                                                <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: '#93c5fd', flexShrink: 0 }} />
                                                <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 600, fontSize: '0.82rem', color: '#2563eb' }}>
                                                    {formatDuration(roadData.summary.duration)}
                                                </Typography>
                                                <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.72rem', color: '#64748b' }}>
                                                    {t('routes.road_path_total')}
                                                </Typography>
                                            </Box>
                                        )}

                                        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                                            <Box sx={{ flex: '1 1 400px', minWidth: 280 }}>
                                                <RoutePreviewMap
                                                    stops={route.stops}
                                                    schoolLat={profile?.schoollatitude}
                                                    schoolLng={profile?.schoollongitude}
                                                    routeType={route.route_type}
                                                    depotLat={route.depot_latitude}
                                                    depotLng={route.depot_longitude}
                                                    onPathLoad={(data) => setRoadData(data ? { routeId: route.id, ...data } : null)}
                                                />
                                            </Box>
                                            <Box sx={{ flex: '1 1 280px', minWidth: 220 }}>
                                                <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 600, fontSize: '0.875rem', color: NAV_BG, mb: 1 }}>
                                                    {t('routes.stop_order')}
                                                </Typography>
                                                <List dense disablePadding>
                                                    {route.stops.map((stop, idx) => {
                                                        const isAbsent = todayAbsentChildIds.has(stop.child_id);
                                                        const leg = roadData?.routeId === route.id
                                                            ? getLegForStop(idx, route.route_type, roadData.legs)
                                                            : null;
                                                        const arrivalTime = (roadData?.routeId === route.id && route.start_time && route.route_type !== 2)
                                                            ? getArrivalTime(route.start_time, idx, roadData.legs)
                                                            : null;
                                                        return (
                                                        <ListItem key={stop.routepoint_id} sx={{ py: 0.5, px: 1, borderRadius: '8px', mb: 0.5, bgcolor: isAbsent ? '#fff7ed' : '#f8fafc', border: isAbsent ? '1px solid #fed7aa' : '1px solid transparent' }}>
                                                            <Box sx={{
                                                                width: 24, height: 24, borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                backgroundColor: isAbsent ? '#f97316' : '#2563eb', color: '#fff', fontSize: '0.7rem', fontWeight: 700, mr: 1.5, flexShrink: 0,
                                                            }}>
                                                                {idx + 1}
                                                            </Box>
                                                            <ListItemText
                                                                primary={
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                                        <RouterLink
                                                                            to={`/student/${stop.child_id}`}
                                                                            target="_blank"
                                                                            rel="noopener noreferrer"
                                                                            onClick={(e) => e.stopPropagation()}
                                                                            style={{ color: 'inherit', textDecoration: 'none' }}
                                                                            onMouseEnter={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
                                                                            onMouseLeave={(e) => { e.currentTarget.style.textDecoration = 'none'; }}
                                                                        >
                                                                            {stop.child_firstname} {stop.child_lastname}
                                                                        </RouterLink>
                                                                        {isAbsent && (
                                                                            <Chip label={t('absences.absent_today')} size="small" sx={{ height: 16, fontSize: '0.6rem', backgroundColor: '#f97316', color: '#fff', fontWeight: 700 }} />
                                                                        )}
                                                                    </Box>
                                                                }
                                                                secondary={
                                                                    <>
                                                                        <span style={{ display: 'block' }}>{stop.address || stop.name}</span>
                                                                        {leg && (
                                                                            <span style={{ display: 'block', fontFamily: '"Outfit", sans-serif', fontSize: '0.65rem', color: '#2563eb', fontWeight: 600, marginTop: 2 }}>
                                                                                {formatDistance(leg.distance)} · {formatDuration(leg.duration)}
                                                                            </span>
                                                                        )}
                                                                        {arrivalTime && (
                                                                            <span style={{ display: 'block', fontFamily: '"Outfit", sans-serif', fontSize: '0.65rem', color: '#16a34a', fontWeight: 700, marginTop: 2 }}>
                                                                                ~{arrivalTime}
                                                                            </span>
                                                                        )}
                                                                    </>
                                                                }
                                                                primaryTypographyProps={{ fontFamily: '"Outfit", sans-serif', fontWeight: 600, fontSize: '0.8rem', color: NAV_BG }}
                                                                secondaryTypographyProps={{ component: 'div', fontFamily: '"Outfit", sans-serif', fontSize: '0.7rem', color: '#64748b' }}
                                                            />
                                                            <Tooltip title={t('absences.manage')}>
                                                                <IconButton size="small" onClick={() => openAbsenceDialog(stop)} sx={{ p: 0.5, color: '#94a3b8', '&:hover': { color: '#f97316' } }}>
                                                                    <CalendarMonthIcon sx={{ fontSize: 15 }} />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </ListItem>
                                                        );
                                                    })}
                                                </List>
                                            </Box>
                                        </Box>
                                    </Box>
                                )}
                            </Paper>
                        ))}
                    </Box>
                ))}
            </Container>

            {/* ── Route Editor Dialog ── */}
            <DndProvider backend={HTML5Backend}>
            <Dialog
                open={editorOpen}
                onClose={() => setEditorOpen(false)}
                maxWidth="xl"
                fullWidth
                PaperProps={{ sx: { borderRadius: '16px', maxHeight: '95vh' } }}
            >
                <DialogTitle sx={{
                    fontFamily: '"Outfit", sans-serif', fontWeight: 700, color: NAV_BG,
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    pb: 1,
                }}>
                    {editingRoute ? t('routes.edit_route') : t('routes.new_route')}
                    <IconButton onClick={() => setEditorOpen(false)}><CloseIcon /></IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>

                    {/* Route name + type + start time row */}
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mt: 1 }}>
                        <TextField
                            label={t('routes.route_name')}
                            value={routeName}
                            onChange={(e) => setRouteName(e.target.value)}
                            size="small"
                            required
                            sx={{ minWidth: 240 }}
                        />
                        <ToggleButtonGroup
                            value={routeType} exclusive
                            onChange={(_, val) => { if (!editingRoute && val !== null) { setRouteType(val); setSelectedStops([]); setDepotLat(null); setDepotLng(null); setDepotAddress(''); setPickingDepot(false); } }}
                            size="small"
                            disabled={!!editingRoute}
                        >
                            <ToggleButton value={0} sx={{ textTransform: 'none', fontFamily: '"Outfit", sans-serif', gap: 0.5 }}>
                                <SchoolIcon fontSize="small" /> {t('routes.to_school')}
                            </ToggleButton>
                            <ToggleButton value={1} sx={{ textTransform: 'none', fontFamily: '"Outfit", sans-serif', gap: 0.5 }}>
                                <HomeIcon fontSize="small" /> {t('routes.from_school')}
                            </ToggleButton>
                            <ToggleButton value={2} sx={{ textTransform: 'none', fontFamily: '"Outfit", sans-serif', gap: 0.5 }}>
                                <EventIcon fontSize="small" /> {t('routes.one_time')}
                            </ToggleButton>
                        </ToggleButtonGroup>
                        <Tooltip title={t('routes.start_time_hint')} arrow>
                            <TextField
                                label={t('routes.start_time')}
                                type="time"
                                value={routeStartTime}
                                onChange={(e) => setRouteStartTime(e.target.value)}
                                size="small"
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ step: 60 }}
                                sx={{ width: 148 }}
                            />
                        </Tooltip>
                    </Box>

                    {/* Depot row — pickup routes only */}
                    {routeType === 0 && (
                        <Box sx={{
                            display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap',
                            px: 1.5, py: 1, borderRadius: '10px',
                            bgcolor: AMBER_GLOW, border: `1px solid ${AMBER}`,
                        }}>
                            <DirectionsBusIcon sx={{ color: AMBER_DARK, fontSize: 18, flexShrink: 0 }} />
                            <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 600, fontSize: '0.82rem', color: NAV_BG, flexShrink: 0 }}>
                                {t('routes.depot_label')}:
                            </Typography>
                            {depotLat ? (
                                <Chip
                                    label={depotAddress || `${Number(depotLat).toFixed(4)}, ${Number(depotLng).toFixed(4)}`}
                                    size="small"
                                    onDelete={() => { setDepotLat(null); setDepotLng(null); setDepotAddress(''); }}
                                    sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 600, fontSize: '0.72rem', bgcolor: AMBER, color: NAV_BG, maxWidth: 260 }}
                                />
                            ) : (
                                <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.78rem', color: '#94a3b8', fontStyle: 'italic' }}>
                                    {pickingDepot ? t('routes.depot_picking_hint') : t('routes.depot_not_set')}
                                </Typography>
                            )}
                            <Button
                                size="small"
                                variant={pickingDepot ? 'contained' : 'outlined'}
                                onClick={() => setPickingDepot(prev => !prev)}
                                sx={{
                                    ml: 'auto', fontFamily: '"Outfit", sans-serif', textTransform: 'none',
                                    fontSize: '0.75rem', borderRadius: '8px',
                                    ...(pickingDepot
                                        ? { bgcolor: AMBER_DARK, color: '#fff', '&:hover': { bgcolor: AMBER } }
                                        : { borderColor: AMBER, color: AMBER_DARK, '&:hover': { borderColor: AMBER_DARK, bgcolor: AMBER_GLOW } }
                                    ),
                                }}
                            >
                                {pickingDepot ? t('routes.depot_cancel_pick') : t('routes.depot_set_on_map')}
                            </Button>
                        </Box>
                    )}

                    {/* Main content: map + panels */}
                    <Box sx={{ display: 'flex', gap: 2, flex: 1 }}>

                        {/* Left: map + filter + children list */}
                        <Box sx={{ flex: '1 1 500px', minWidth: 340, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                                <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 600, fontSize: '0.85rem', color: NAV_BG, flex: 1 }}>
                                    {t('routes.available_children')}
                                    {mapChildren.length > 0 && (
                                        <Typography component="span" sx={{ ml: 1, fontWeight: 400, color: '#64748b', fontSize: '0.75rem' }}>
                                            ({mapChildren.length} {t('routes.available')}) — {t('routes.map_hint')}
                                        </Typography>
                                    )}
                                </Typography>
                                <Button
                                    size="small"
                                    variant={editorRoutePath ? 'contained' : 'outlined'}
                                    onClick={toggleEditorPath}
                                    disabled={editorLoadingPath || selectedStops.filter(s => s.latitude && s.longitude).length < 2}
                                    startIcon={editorLoadingPath ? <CircularProgress size={12} sx={{ color: 'inherit' }} /> : <RouteIcon sx={{ fontSize: 14 }} />}
                                    sx={{
                                        fontFamily: '"Outfit", sans-serif', fontWeight: 600, fontSize: '0.72rem',
                                        textTransform: 'none', borderRadius: '8px', py: 0.5, px: 1.5,
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)', flexShrink: 0,
                                        ...(editorRoutePath
                                            ? { bgcolor: '#2563eb', color: '#fff', '&:hover': { bgcolor: '#1d4ed8' } }
                                            : { borderColor: '#2563eb', color: '#2563eb', '&:hover': { bgcolor: '#eff6ff' } }
                                        ),
                                    }}
                                >
                                    {editorLoadingPath ? t('routes.road_path_loading') : editorRoutePath ? t('routes.hide_road_path') : t('routes.show_road_path')}
                                </Button>
                                {editorPathData && (
                                    <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.75rem', color: '#64748b', flexShrink: 0 }}>
                                        {formatDistance(editorPathData.summary.distance)} · {formatDuration(editorPathData.summary.duration)}
                                    </Typography>
                                )}
                            </Box>

                            {/* Interactive map */}
                            {editorOpen && (
                                <EditorMap
                                    schoolLat={profile?.schoollatitude}
                                    schoolLng={profile?.schoollongitude}
                                    availableChildren={availableChildren}
                                    selectedStops={selectedStops}
                                    onTogglePoi={togglePoi}
                                    depotLat={routeType === 0 ? depotLat : null}
                                    depotLng={routeType === 0 ? depotLng : null}
                                    pickingDepot={pickingDepot}
                                    onMapClick={handleMapClick}
                                    routePath={editorRoutePath}
                                />
                            )}

                            {/* Filters */}
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                <FormControl size="small" sx={{ minWidth: 140 }}>
                                    <InputLabel>{t('routes.filter_class')}</InputLabel>
                                    <Select
                                        value={filterClass}
                                        onChange={(e) => { setFilterClass(e.target.value); setFilterDepartment(''); }}
                                        label={t('routes.filter_class')}
                                    >
                                        <MenuItem value="">{t('routes.all_classes')}</MenuItem>
                                        {classes.map(c => <MenuItem key={c.id} value={String(c.id)}>{c.name}</MenuItem>)}
                                    </Select>
                                </FormControl>
                                {filterClass && departmentsForClass.length > 0 && (
                                    <FormControl size="small" sx={{ minWidth: 140 }}>
                                        <InputLabel>{t('routes.filter_dept')}</InputLabel>
                                        <Select
                                            value={filterDepartment}
                                            onChange={(e) => setFilterDepartment(e.target.value)}
                                            label={t('routes.filter_dept')}
                                        >
                                            <MenuItem value="">{t('routes.all_depts')}</MenuItem>
                                            {departmentsForClass.map(d => <MenuItem key={d.id} value={String(d.id)}>{d.name}</MenuItem>)}
                                        </Select>
                                    </FormControl>
                                )}
                                {(filterClass || filterDepartment) && (
                                    <Button size="small" onClick={() => { setFilterClass(''); setFilterDepartment(''); }}
                                        sx={{ textTransform: 'none', color: '#64748b', fontFamily: '"Outfit", sans-serif' }}>
                                        {t('routes.clear_filter')}
                                    </Button>
                                )}
                            </Box>

                            {/* Children list */}
                            <Box sx={{ maxHeight: 260, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                {availableChildren.length === 0 ? (
                                    <Alert severity="info" sx={{ borderRadius: '10px', fontFamily: '"Outfit", sans-serif' }}>
                                        {filterClass || filterDepartment ? t('routes.no_children_in_filter') : t('routes.no_available_children')}
                                    </Alert>
                                ) : (
                                    availableChildren.map(child => (
                                        <Paper key={child.child_id} sx={{ p: 1.25, borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                                            <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 600, fontSize: '0.82rem', color: NAV_BG }}>
                                                {child.firstname} {child.lastname}
                                                <Typography component="span" sx={{ ml: 1, color: '#94a3b8', fontSize: '0.7rem', fontWeight: 400 }}>
                                                    {child.schoolclass_name}{child.department_name ? ` · ${child.department_name}` : ''}
                                                </Typography>
                                            </Typography>
                                            <Box sx={{ mt: 0.5, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                                                {child.pois.map(poi => {
                                                    const isSelected = selectedPoiIds.has(poi.poi_id);
                                                    return (
                                                        <Box
                                                            key={poi.poi_id}
                                                            onClick={() => togglePoi(poi, child)}
                                                            sx={{
                                                                display: 'flex', alignItems: 'center', gap: 0.75, p: '5px 8px',
                                                                borderRadius: '7px', cursor: 'pointer',
                                                                bgcolor: isSelected ? '#2563eb0f' : 'transparent',
                                                                border: `1px solid ${isSelected ? '#2563eb' : 'transparent'}`,
                                                                transition: 'all 0.12s',
                                                                '&:hover': { bgcolor: isSelected ? '#2563eb1a' : '#f1f5f9' },
                                                            }}
                                                        >
                                                            <Box sx={{
                                                                width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                                                                backgroundColor: isSelected ? '#2563eb' : '#94a3b8',
                                                            }} />
                                                            <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.72rem', color: isSelected ? '#2563eb' : '#64748b', flex: 1 }}>
                                                                {poi.address || poi.name}
                                                            </Typography>
                                                            {isSelected ? (
                                                                <Chip label={t('routes.added')} size="small" sx={{
                                                                    height: 17, fontSize: '0.62rem',
                                                                    backgroundColor: '#2563eb', color: '#fff',
                                                                }} />
                                                            ) : (
                                                                <Typography sx={{ fontSize: '0.65rem', color: '#94a3b8', fontFamily: '"Outfit", sans-serif' }}>
                                                                    + {t('routes.add_stop')}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    );
                                                })}
                                            </Box>
                                        </Paper>
                                    ))
                                )}
                            </Box>
                        </Box>

                        {/* Right: ordered stops list */}
                        <Box sx={{ flex: '0 0 280px', minWidth: 240, display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 600, fontSize: '0.85rem', color: NAV_BG, flex: 1 }}>
                                    {t('routes.route_stops')}
                                    <Typography component="span" sx={{ ml: 1, fontWeight: 400, color: '#64748b', fontSize: '0.75rem' }}>
                                        ({selectedStops.length})
                                    </Typography>
                                </Typography>
                                {preOptimizeStops && (
                                    <Tooltip title={t('routes.optimize_undo')} arrow>
                                        <IconButton
                                            size="small"
                                            onClick={() => { setSelectedStops(preOptimizeStops); setPreOptimizeStops(null); }}
                                            sx={{ p: 0.5, color: '#64748b', '&:hover': { color: NAV_BG } }}
                                        >
                                            <UndoIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                    </Tooltip>
                                )}
                                <Tooltip title={selectedStops.length < 2 ? t('routes.optimize_too_few') : t('routes.optimize')} arrow>
                                    <span>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={optimizing ? <CircularProgress size={12} /> : <AutoFixHighIcon sx={{ fontSize: 14 }} />}
                                            onClick={handleOptimize}
                                            disabled={optimizing || selectedStops.length < 2}
                                            sx={{
                                                fontFamily: '"Outfit", sans-serif', textTransform: 'none', fontWeight: 600,
                                                fontSize: '0.72rem', borderRadius: '8px', py: 0.4, px: 1,
                                                borderColor: AMBER, color: AMBER_DARK,
                                                '&:hover': { borderColor: AMBER_DARK, backgroundColor: AMBER_GLOW },
                                                '&.Mui-disabled': { borderColor: '#e2e8f0', color: '#94a3b8' },
                                            }}
                                        >
                                            {optimizing ? t('routes.optimizing') : t('routes.optimize')}
                                        </Button>
                                    </span>
                                </Tooltip>
                            </Box>

                            {selectedStops.length === 0 ? (
                                <Alert severity="info" sx={{ borderRadius: '10px', fontFamily: '"Outfit", sans-serif', fontSize: '0.8rem' }}>
                                    {t('routes.select_stops_hint')}
                                </Alert>
                            ) : (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, overflowY: 'auto', maxHeight: 680 }}>
                                    {selectedStops.map((stop, idx) => {
                                        const leg = editorPathData ? getLegForStop(idx, routeType, editorPathData.legs) : null;
                                        const arrivalTime = (editorPathData && routeStartTime && routeType !== 2)
                                            ? getArrivalTime(routeStartTime, idx, editorPathData.legs)
                                            : null;
                                        return (
                                            <Box key={stop.poi_id}>
                                                <DraggableStop
                                                    stop={stop}
                                                    idx={idx}
                                                    total={selectedStops.length}
                                                    onMove={reorderStop}
                                                    onRemove={removeStop}
                                                />
                                                {leg && (
                                                    <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.65rem', color: '#64748b', pl: 1, mt: 0.25, mb: 0 }}>
                                                        {formatDistance(leg.distance)} · {formatDuration(leg.duration)}
                                                    </Typography>
                                                )}
                                                {arrivalTime && (
                                                    <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.65rem', color: '#16a34a', fontWeight: 700, pl: 1, mt: 0, mb: 0.25 }}>
                                                        ~{arrivalTime}
                                                    </Typography>
                                                )}
                                            </Box>
                                        );
                                    })}
                                </Box>
                            )}
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 2.5, pb: 2 }}>
                    <Button onClick={() => setEditorOpen(false)} sx={{ fontFamily: '"Outfit", sans-serif', textTransform: 'none', color: '#64748b' }}>
                        {t('common.cancel')}
                    </Button>
                    <Tooltip
                        title={
                            !routeName.trim() && selectedStops.length === 0
                                ? t('routes.save_requires_name_and_stops')
                                : !routeName.trim()
                                ? t('routes.save_requires_name')
                                : selectedStops.length === 0
                                ? t('routes.save_requires_stops')
                                : ''
                        }
                        disableHoverListener={!(!routeName.trim() || selectedStops.length === 0)}
                    >
                        <span>
                            <Button
                                variant="contained" onClick={handleSaveRoute}
                                disabled={!routeName.trim() || selectedStops.length === 0}
                                sx={{
                                    fontFamily: '"Outfit", sans-serif', textTransform: 'none', fontWeight: 600,
                                    background: `linear-gradient(135deg, ${AMBER} 0%, ${AMBER_DARK} 100%)`,
                                    color: NAV_BG, borderRadius: '10px',
                                    '&:hover': { background: `linear-gradient(135deg, #fbbf24 0%, ${AMBER} 100%)` },
                                    '&.Mui-disabled': { background: '#e2e8f0', color: '#94a3b8' },
                                }}
                            >
                                {t('common.save')}
                            </Button>
                        </span>
                    </Tooltip>
                </DialogActions>
            </Dialog>
            </DndProvider>

            {/* ── Absence Management Dialog ── */}
            <Dialog open={absenceDialogOpen} onClose={() => setAbsenceDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
                <DialogTitle sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, color: NAV_BG, display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CalendarMonthIcon sx={{ color: '#f97316', fontSize: 20 }} />
                        {absenceChild ? t('absences.for', { name: absenceChild.name }) : t('absences.title')}
                    </Box>
                    <IconButton size="small" onClick={() => setAbsenceDialogOpen(false)}><CloseIcon fontSize="small" /></IconButton>
                </DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                    {/* Add new absence */}
                    <Box sx={{ p: 2, borderRadius: '12px', bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                        <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 600, fontSize: '0.82rem', color: NAV_BG, mb: 1.5 }}>
                            {t('absences.add_absence')}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                            <TextField
                                label={t('absences.date_label')}
                                type="date"
                                size="small"
                                value={absenceDate}
                                onChange={(e) => setAbsenceDate(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ min: localDateString() }}
                                sx={{ minWidth: 160, fontFamily: '"Outfit", sans-serif' }}
                            />
                            <TextField
                                label={t('absences.reason_label')}
                                placeholder={t('absences.reason_placeholder')}
                                size="small"
                                value={absenceReason}
                                onChange={(e) => setAbsenceReason(e.target.value)}
                                sx={{ flex: 1, minWidth: 160 }}
                            />
                            <Button
                                variant="contained"
                                disabled={!absenceDate || absenceSaving}
                                onClick={handleAddAbsence}
                                sx={{
                                    fontFamily: '"Outfit", sans-serif', textTransform: 'none', fontWeight: 600,
                                    backgroundColor: '#f97316', '&:hover': { backgroundColor: '#ea6f0e' },
                                    borderRadius: '8px', whiteSpace: 'nowrap',
                                    '&.Mui-disabled': { backgroundColor: '#e2e8f0', color: '#94a3b8' },
                                }}
                            >
                                {t('absences.save')}
                            </Button>
                        </Box>
                    </Box>

                    {/* Existing absences list */}
                    <Box>
                        <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 600, fontSize: '0.82rem', color: NAV_BG, mb: 1 }}>
                            {t('absences.title')}
                            <Typography component="span" sx={{ ml: 1, fontWeight: 400, color: '#64748b', fontSize: '0.75rem' }}>
                                ({absenceList.length})
                            </Typography>
                        </Typography>
                        {absenceList.length === 0 ? (
                            <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.78rem', color: '#94a3b8', fontStyle: 'italic' }}>
                                {t('absences.no_absences')}
                            </Typography>
                        ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, maxHeight: 260, overflowY: 'auto' }}>
                                {absenceList.map((a) => (
                                    <Box key={a.absence_id} sx={{ display: 'flex', alignItems: 'center', gap: 1, p: '8px 12px', borderRadius: '10px', bgcolor: '#fff7ed', border: '1px solid #fed7aa' }}>
                                        <CalendarMonthIcon sx={{ fontSize: 15, color: '#f97316', flexShrink: 0 }} />
                                        <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 600, fontSize: '0.8rem', color: '#c2410c', flex: 1 }}>
                                            {new Date(a.date + 'T00:00:00').toLocaleDateString(undefined, { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })}
                                            {a.reason && (
                                                <Typography component="span" sx={{ ml: 1, fontWeight: 400, color: '#9a3412', fontSize: '0.72rem' }}>
                                                    — {a.reason}
                                                </Typography>
                                            )}
                                        </Typography>
                                        <IconButton size="small" onClick={() => handleDeleteAbsence(a.absence_id)} sx={{ p: 0.5, color: '#94a3b8', '&:hover': { color: '#ef4444' } }}>
                                            <DeleteIcon sx={{ fontSize: 14 }} />
                                        </IconButton>
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 2.5, pb: 2 }}>
                    <Button onClick={() => setAbsenceDialogOpen(false)} sx={{ fontFamily: '"Outfit", sans-serif', textTransform: 'none', color: '#64748b' }}>
                        {t('common.cancel')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Delete Confirm Dialog ── */}
            <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} PaperProps={{ sx: { borderRadius: '16px' } }}>
                <DialogTitle sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 600, color: NAV_BG }}>
                    {t('routes.delete')}
                </DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily: '"Outfit", sans-serif', color: '#64748b' }}>
                        {t('routes.delete_confirm', { name: deleteRoute?.name })}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setDeleteOpen(false)} sx={{ textTransform: 'none', fontFamily: '"Outfit", sans-serif' }}>{t('common.cancel')}</Button>
                    <Button variant="contained" onClick={handleDelete} sx={{
                        textTransform: 'none', fontFamily: '"Outfit", sans-serif', fontWeight: 600,
                        backgroundColor: '#ef4444', borderRadius: '10px', '&:hover': { backgroundColor: '#dc2626' },
                    }}>
                        {t('common.delete')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={snack.open} autoHideDuration={20000} onClose={() => setSnack(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))} sx={{ borderRadius: '10px' }}>
                    {snack.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default RoutesPage;
